import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  checkResult, clearRuns, DEPTHS, estimateRunCost, fetchDefaultPrompt, fetchLabModels, fetchModelScores,
  fetchVerdicts, LANGUAGES, loadRuns, loadSelection, MIN_FEEDBACK, saveSelection, sendVerdict,
  UNKNOWN_PLACEHOLDER, VIBES,
  type GameMode, type LabInput, type LabModel, type LabResult, type LabRun, type ModelScore, type Verdict,
} from '../lib/lab';
import { isRunning, retryModel, showRun, startRun, useLabRun } from '../lib/labRun';

const DRAFT = 'lab.draft';
const EXAMPLE = 'We all went to college together, work in tech, and love hiking. We\'re pretty open with each other and love roasting each other.';

const DEFAULT_INPUT: LabInput = {
  groupDescription: '', vibe: 2, depth: 2, gameMode: 'personalized', language: 'en',
};

function rememberedInput(): LabInput {
  try { return { ...DEFAULT_INPUT, ...JSON.parse(localStorage.getItem(DRAFT) ?? '{}') }; } catch { return DEFAULT_INPUT; }
}

/**
 * One prompt, every model, side by side.
 *
 * The form is the app's own customize screen in a different coat: the same
 * five vibes, the same five depths, the same two modes. What comes back is
 * exactly what a player would have got — so the comparison is about the
 * models, not about the harness.
 */
export type LabView = 'compare' | 'scores';

export default function Lab({ meId, view }: { meId: string; view: LabView }) {
  if (view === 'scores') return <Scoreboard />;
  return <Compare meId={meId} />;
}

function Compare({ meId }: { meId: string }) {
  const [models, setModels] = useState<LabModel[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [input, setInput] = useState<LabInput>(rememberedInput);
  // The run lives in a store that outlives this screen (see lib/labRun.ts),
  // so leaving for a queue mid-comparison loses nothing.
  const run = useLabRun();
  const [history, setHistory] = useState<LabRun[]>(loadRuns);
  // Your verdicts on the run on screen, by model. Loaded from the database
  // when the run changes, so reopening an old run shows what you said then.
  const [verdicts, setVerdicts] = useState<Record<string, Verdict>>({});
  const [verdictError, setVerdictError] = useState<string | null>(null);

  useEffect(() => {
    fetchLabModels()
      .then((list) => { setModels(list); setSelected(loadSelection(list)); })
      .catch((e) => setModelsError(e instanceof Error ? e.message : String(e)));
  }, []);

  const chosen = useMemo(
    () => (models ?? []).filter((m) => selected.includes(m.id)),
    [models, selected],
  );
  const toggleModel = (id: string) => setSelected((cur) => {
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    saveSelection(next);
    return next;
  });
  const selectAll = (ids: string[]) => { setSelected(ids); saveSelection(ids); };
  const runCost = chosen.reduce((sum, m) => sum + estimateRunCost(m), 0);

  useEffect(() => {
    try { localStorage.setItem(DRAFT, JSON.stringify(input)); } catch { /* ignore */ }
  }, [input]);

  const running = isRunning(run);
  const canRun = chosen.length > 0 && input.groupDescription.trim().length >= 10 && !running;

  useEffect(() => {
    if (!run) { setVerdicts({}); return; }
    let alive = true;
    fetchVerdicts(meId, run.id).then((v) => { if (alive) setVerdicts(v); }).catch(() => { /* shown as unset */ });
    return () => { alive = false; };
  }, [meId, run?.id]);

  const judge = useCallback(async (result: LabResult, verdict: Verdict) => {
    if (!run) return;
    const next: Verdict | null = verdicts[result.model] === verdict ? null : verdict;
    setVerdicts((v) => {
      const copy = { ...v };
      if (next) copy[result.model] = next; else delete copy[result.model];
      return copy;
    });
    setVerdictError(null);
    try { await sendVerdict(meId, run, result, next); }
    catch (e) { setVerdictError(e instanceof Error ? e.message : String(e)); }
  }, [meId, run, verdicts]);

  // History only changes when a run finishes, and the store writes it; so
  // re-read it whenever the run stops being "running".
  useEffect(() => { if (!running) setHistory(loadRuns()); }, [running, run?.id]);

  const start = useCallback(() => {
    if (!canRun) return;
    void startRun(chosen, { ...input, groupDescription: input.groupDescription.trim() });
  }, [chosen, canRun, input]);

  const patch = (p: Partial<LabInput>) => setInput((i) => ({ ...i, ...p }));

  const retry = useCallback((modelId: string) => { void retryModel(modelId); }, []);

  return (
    <>
      <div className="phead">
        <div>
          <h1>Compare models</h1>
          <p>
            Describe a group the way a player would, pick the same settings they would, and
            see what every model we support gives back — side by side, with the mechanical
            checks the app cares about done for you.
          </p>
        </div>
      </div>

      {modelsError && (
        <div className="banner" style={{ background: 'var(--crit-soft)', borderColor: 'var(--crit-line)' }}>
          <span style={{ color: 'var(--crit)', fontWeight: 800 }}>!</span>
          <span><span className="t" style={{ color: 'var(--crit)' }}>Could not load the model list</span>
            <span className="d">{modelsError}</span></span>
        </div>
      )}

      <div className="panel lab-form">
        <div className="field">
          <label htmlFor="lab-desc">Group description</label>
          <textarea
            id="lab-desc" className="note lab-desc" rows={4}
            placeholder={`e.g. ${EXAMPLE}`}
            value={input.groupDescription}
            onChange={(e) => patch({ groupDescription: e.target.value })}
          />
          <span className="hint">
            The user message, verbatim. Everything else below becomes the system prompt.
            {input.groupDescription.trim().length < 10 && ' At least 10 characters.'}
            {!input.groupDescription && (
              <> <button className="linkbtn" onClick={() => patch({ groupDescription: EXAMPLE })}>Use the example</button></>
            )}
          </span>
        </div>

        <div className="lab-settings">
          <Choice label="Vibe" value={input.vibe} options={VIBES.map((v) => v)} onPick={(vibe) => patch({ vibe })} />
          <Choice label="Depth" value={input.depth} options={DEPTHS.map((d) => d.label)} onPick={(depth) => patch({ depth })} />
          <div className="field">
            <label>Mode</label>
            <div className="seg">
              {(['personalized', 'general'] as GameMode[]).map((m) => (
                <button key={m} aria-pressed={input.gameMode === m} onClick={() => patch({ gameMode: m })}>
                  {m === 'personalized' ? 'Personalized' : 'General'}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <label htmlFor="lab-lang">Language</label>
            <select id="lab-lang" className="inp lab-select" value={input.language}
              onChange={(e) => patch({ language: e.target.value })}>
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </div>

        <PromptEditor input={input} onChange={(systemPrompt) => patch({ systemPrompt })} />

        <div className="field">
          <label>
            Models
            {models && (
              <span className="lab-modelacts">
                <button className="linkbtn" onClick={() => selectAll(models.map((m) => m.id))}>all</button>
                <button className="linkbtn" onClick={() => selectAll(models.filter((m) => m.tier === 'large').map((m) => m.id))}>large only</button>
                <button className="linkbtn" onClick={() => selectAll([])}>none</button>
              </span>
            )}
          </label>
          {!models && !modelsError && <span className="hint">Loading models…</span>}
          {models && (
            <div className="lab-models">
              {models.map((m) => (
                <button
                  key={m.id} className="lab-modelchip" aria-pressed={selected.includes(m.id)}
                  onClick={() => toggleModel(m.id)}
                  title={`${m.id} · ${m.provider} · €${m.price[0].toFixed(2)} in / €${m.price[1].toFixed(2)} out per 1M tokens`}
                >
                  <span className="lab-modelname">{m.label}</span>
                  <span className="lab-modelmeta">
                    {m.maker} · {m.tier}{m.isDefault ? ' · in the app' : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lab-run">
          <button className="btn pri" disabled={!canRun} onClick={start}>
            {running ? 'Running…' : `Run on ${chosen.length} model${chosen.length === 1 ? '' : 's'}`}
          </button>
          <span className="hint">
            {chosen.length === 0
              ? 'Pick at least one model.'
              : <>About <b>€{runCost < 0.01 ? '<0.01' : runCost.toFixed(2)}</b> per run at these sizes — each one is a real generation, the same a player would trigger.</>}
          </span>
        </div>
      </div>

      {verdictError && (
        <div className="banner" style={{ background: 'var(--crit-soft)', borderColor: 'var(--crit-line)' }}>
          <span style={{ color: 'var(--crit)', fontWeight: 800 }}>!</span>
          <span><span className="t" style={{ color: 'var(--crit)' }}>Could not save your verdict</span>
            <span className="d">{verdictError}</span></span>
        </div>
      )}

      {run && <RunView run={run} verdicts={verdicts} onJudge={judge} onRetry={retry} />}

      {history.length > 0 && (
        <div className="lab-history">
          <div className="grp-h">
            Recent runs
            <span className="hint" style={{ fontWeight: 500 }}>kept in this browser</span>
            <button className="linkbtn" style={{ marginLeft: 'auto' }}
              onClick={() => { clearRuns(); setHistory([]); }}>Clear</button>
          </div>
          <div className="lab-runs">
            {history.map((h) => (
              <button key={h.id} className="lab-runrow" aria-current={run?.id === h.id}
                disabled={running}
                onClick={() => { showRun(h); setInput(h.input); }}>
                <span className="when">{new Date(h.at).toLocaleString()}</span>
                <span className="desc">{h.input.groupDescription}</span>
                <span className="meta">
                  {VIBES[h.input.vibe]} · {DEPTHS[h.input.depth]?.label} · {h.input.gameMode} · {h.input.language}
                  {h.input.systemPrompt && ' · custom prompt'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The system prompt, editable.
 *
 * Closed, it says the run uses the app's own prompt for the settings above.
 * Open, it shows exactly that prompt, composed by the server for the current
 * vibe, depth, mode and language, and any edit becomes the prompt sent —
 * verbatim, to every model in the run. Reset puts the app's prompt back and
 * stops sending an override.
 */
function PromptEditor({ input, onChange }: { input: LabInput; onChange: (p: string | undefined) => void }) {
  const [open, setOpen] = useState(!!input.systemPrompt);
  const [base, setBase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { vibe, depth, gameMode, language } = input;

  // The app's prompt changes with the settings; keep the reference current
  // so "reset" and "is this edited?" always mean the prompt for *these*.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setError(null);
    fetchDefaultPrompt({ ...input, systemPrompt: undefined })
      .then((p) => { if (alive) setBase(p); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vibe, depth, gameMode, language]);

  const edited = input.systemPrompt !== undefined;
  const shown = input.systemPrompt ?? base ?? '';

  return (
    <div className={`field lab-prompt${edited ? ' edited' : ''}`}>
      <label>
        System prompt
        <span className="lab-modelacts">
          {edited && <span className="pill p-warn">custom</span>}
          <button className="linkbtn" onClick={() => setOpen((v) => !v)}>{open ? 'hide' : edited ? 'edit' : 'customize…'}</button>
          {edited && <button className="linkbtn" onClick={() => { onChange(undefined); setOpen(true); }}>reset to the app's</button>}
        </span>
      </label>
      {!open && (
        <span className="hint">
          {edited
            ? 'A custom prompt is in use — the vibe, depth, mode and language above only label the run.'
            : 'The app composes its prompt from the settings above. Open it to try a different one.'}
        </span>
      )}
      {open && error && <span className="hint" style={{ color: 'var(--crit)' }}>{error}</span>}
      {open && !error && base === null && !edited && <span className="hint">Loading the app's prompt…</span>}
      {open && (base !== null || edited) && (
        <>
          <textarea
            className="note lab-promptbox mono" spellCheck={false}
            value={shown}
            onChange={(e) => onChange(e.target.value === base ? undefined : e.target.value)}
          />
          <span className="hint">
            {edited
              ? 'Sent verbatim as the system message to every model in the run. The group description is still the user message.'
              : 'This is the app\'s prompt for the current settings. Change anything and the run uses your version instead.'}
          </span>
        </>
      )}
    </div>
  );
}

function Choice({ label, value, options, onPick }: {
  label: string; value: number; options: readonly string[]; onPick: (i: number) => void;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="seg lab-seg">
        {options.map((o, i) => (
          <button key={o} aria-pressed={value === i} onClick={() => onPick(i)}>{o}</button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ results */

function RunView({ run, verdicts, onJudge, onRetry }: {
  run: LabRun;
  verdicts: Record<string, Verdict>;
  onJudge: (result: LabResult, verdict: Verdict) => void;
  onRetry: (model: string) => void;
}) {
  return (
    <div className="lab-grid" style={{ gridTemplateColumns: `repeat(${run.results.length}, minmax(300px, 1fr))` }}>
      {run.results.map((r) => (
        <ResultColumn
          key={r.model} result={r} input={run.input}
          verdict={verdicts[r.model] ?? null}
          onJudge={(v) => onJudge(r, v)}
          onRetry={() => onRetry(r.model)}
        />
      ))}
    </div>
  );
}

function ResultColumn({ result, input, verdict, onJudge, onRetry }: {
  result: LabResult; input: LabInput; verdict: Verdict | null;
  onJudge: (v: Verdict) => void; onRetry: () => void;
}) {
  const issues = useMemo(() => checkResult(result, input), [result, input]);
  const crit = issues.filter((i) => i.severity === 'crit').length;
  const status = result.status === 'running' ? 'running' : result.status === 'failed' ? 'failed' : crit ? 'issues' : 'clean';

  return (
    <section className={`lab-col ${status}`} aria-busy={result.status === 'running'}>
      <header className="lab-colh">
        <span className="lab-model mono">{result.model}</span>
        <span className="right">
          {result.status === 'running' && <span className="pill p-mute">running…</span>}
          {result.status === 'failed' && <span className="pill p-crit">failed</span>}
          {result.status === 'ok' && (
            <>
              <button className="lab-again" title="Run this model again with the same input" onClick={onRetry}>↻</button>
              <span className="pill p-mute num">{result.cards?.length ?? 0} cards</span>
              <span className="pill p-mute num">{(result.ms / 1000).toFixed(1)}s</span>
              <span className={`pill ${crit ? 'p-crit' : issues.length ? 'p-warn' : 'p-ok'}`}>
                {crit ? `${crit} broken` : issues.length ? `${issues.length} to check` : 'clean'}
              </span>
            </>
          )}
        </span>
      </header>

      {result.status === 'failed' && (
        <div className="lab-fail">
          <div className="lab-failmsg">{result.error}</div>
          <button className="btn sm" onClick={onRetry}>Retry this model</button>
        </div>
      )}

      {result.status === 'ok' && (
        <>
          <div className="lab-judge">
            <span className="lab-judgeq">{verdict ? 'Your verdict' : 'Worth playing?'}</span>
            <button className="lab-thumb" aria-pressed={verdict === 'up'} aria-label="Thumbs up"
              title="Good deck — I'd play this" onClick={() => onJudge('up')}>👍</button>
            <button className="lab-thumb down" aria-pressed={verdict === 'down'} aria-label="Thumbs down"
              title="Weak deck" onClick={() => onJudge('down')}>👎</button>
          </div>
          {issues.length > 0 && (
            <ul className="lab-issues">
              {issues.map((i) => (
                <li key={i.text} className={i.severity}>{i.text}</li>
              ))}
            </ul>
          )}
          <div className="lab-sub">
            <span className="slabel">Subtitle</span>
            <span className="lab-subv">{result.subtitle || <i>none</i>}</span>
          </div>
          <ol className="lab-cards">
            {result.cards?.map((c, i) => {
              // Tinted only when the app would show something wrong.
              const broken = UNKNOWN_PLACEHOLDER.test(c.question + c.instruction);
              return (
                <li key={i} className={broken ? 'broken' : undefined}>
                  <div className="lab-q"><Tokens text={c.question} /></div>
                  {c.instruction && <div className="lab-i"><Tokens text={c.instruction} /></div>}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
}

/* ---------------------------------------------------------- scoreboard */

/**
 * Models ranked by how often their decks got a thumbs up. A model only
 * earns a place once enough people have judged it; until then it sits in
 * the pending list with whatever it has so far, so nobody reads a single
 * enthusiastic thumb as a verdict.
 */
function Scoreboard() {
  const [models, setModels] = useState<LabModel[] | null>(null);
  const [scores, setScores] = useState<ModelScore[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchLabModels(), fetchModelScores()])
      .then(([m, sc]) => { setModels(m); setScores(sc); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const rows = useMemo(() => {
    if (!models || !scores) return null;
    const byModel = new Map(scores.map((s) => [s.model, s]));
    const all = models.map((m) => ({ model: m, score: byModel.get(m.id) ?? null }));
    const ranked = all
      .filter((r) => r.score && r.score.total > MIN_FEEDBACK)
      .sort((a, b) => (b.score!.score - a.score!.score) || (b.score!.total - a.score!.total));
    const pending = all
      .filter((r) => !r.score || r.score.total <= MIN_FEEDBACK)
      .sort((a, b) => (b.score?.total ?? 0) - (a.score?.total ?? 0));
    return { ranked, pending };
  }, [models, scores]);

  return (
    <>
      <div className="phead">
        <div>
          <h1>Scoreboard</h1>
          <p>
            Every thumbs up and down from every lab member, rolled up per model. A model is ranked
            once it has more than {MIN_FEEDBACK} verdicts; before that it is still on trial.
          </p>
        </div>
      </div>

      {error && (
        <div className="banner" style={{ background: 'var(--crit-soft)', borderColor: 'var(--crit-line)' }}>
          <span style={{ color: 'var(--crit)', fontWeight: 800 }}>!</span>
          <span><span className="t" style={{ color: 'var(--crit)' }}>Could not load the scores</span>
            <span className="d">{error}</span></span>
        </div>
      )}
      {!rows && !error && <div className="panel"><div className="empty">Loading…</div></div>}

      {rows && (
        <>
          <div className="bsec">
            <div className="bsec-h"><span className="pill p-ok">{rows.ranked.length}</span><span>Ranked</span>
              <span className="d">by share of thumbs up</span></div>
            {rows.ranked.length === 0 && (
              <div className="panel"><div className="empty">
                <b>No model has enough verdicts yet</b>
                Judge a few more comparisons and the first ranking appears here.
              </div></div>
            )}
            {rows.ranked.map((r, i) => <ScoreRow key={r.model.id} place={i + 1} model={r.model} score={r.score!} />)}
          </div>

          <div className="bsec">
            <div className="bsec-h"><span className="pill p-warn">{rows.pending.length}</span><span>New, pending review</span>
              <span className="d">fewer than {MIN_FEEDBACK + 1} verdicts so far</span></div>
            {rows.pending.map((r) => <ScoreRow key={r.model.id} model={r.model} score={r.score} />)}
          </div>
        </>
      )}
    </>
  );
}

function ScoreRow({ place, model, score }: { place?: number; model: LabModel; score: ModelScore | null }) {
  const pct = score ? Math.round(score.score * 100) : null;
  return (
    <div className="lab-scorerow">
      <span className={`lab-place${place && place <= 3 ? ` m${place}` : ''}`}>{place ?? '·'}</span>
      <span className="lab-scorename">
        <span className="nm">{model.label}</span>
        <span className="sub">{model.maker} · {model.tier}{model.isDefault ? ' · in the app' : ''}</span>
      </span>
      <span className="lab-scorebar" aria-hidden="true">
        {pct !== null && <span className="fill" style={{ width: `${pct}%` }} />}
      </span>
      <span className="lab-scorenum num">{pct !== null ? `${pct}%` : '—'}</span>
      <span className="lab-scoretally num">
        {score ? <>👍 {score.ups} · 👎 {score.downs}</> : 'no verdicts yet'}
      </span>
    </div>
  );
}

/** Placeholders drawn the way the translation cards draw them. */
function Tokens({ text }: { text: string }) {
  const parts = text.split(/(\{[^{}]*\})/g);
  return (
    <>
      {parts.map((p, i) => {
        if (!p.startsWith('{')) return <Fragment key={i}>{p}</Fragment>;
        const known = p === '{PLAYER}' || p === '{OTHER_PLAYER}';
        return <span key={i} className={`tok${known ? '' : ' bad'}`}>{p}</span>;
      })}
    </>
  );
}
